// Passive observations only. Nothing in this module repairs or reconnects media.
const finite = (v) => typeof v === "number" && Number.isFinite(v);
const sum = (rows, key) => rows.length && rows.every((r) => finite(r[key]))
  ? rows.reduce((n, r) => n + r[key], 0) : null;

export function audioCounters(report, direction, trackIds) {
  const rows = [...report.values()];
  const byId = new Map(rows.map((r) => [r.id, r]));
  const all = rows.filter((r) => r.type === `${direction === "send" ? "outbound" : "inbound"}-rtp`
    && (r.kind === "audio" || r.mediaType === "audio") && !r.isRemote);
  const trackId = (r) => r.trackIdentifier || byId.get(r.mediaSourceId)?.trackIdentifier
    || byId.get(r.trackId)?.trackIdentifier;
  const matched = all.filter((r) => trackIds.includes(trackId(r)));
  // A multi-track connection with no matching identifiers is unknown, not silence.
  const audio = matched.length ? matched : all.length === 1 && !trackId(all[0]) ? all : [];
  const sources = direction === "send"
    ? [...new Set(audio.map((r) => byId.get(r.mediaSourceId)).filter(Boolean))] : audio;
  const transports = rows.filter((r) => r.type === "transport");
  const selected = transports.map((r) => byId.get(r.selectedCandidatePairId)).filter(Boolean);
  const pairs = selected.length ? selected
    : rows.filter((r) => r.type === "candidate-pair" && r.state === "succeeded"
      && (r.selected === true || r.nominated === true));
  const pair = pairs.length === 1 ? pairs[0] : null;
  const local = byId.get(pair?.localCandidateId);
  const remote = byId.get(pair?.remoteCandidateId);
  const overlay = (r) => /^10\.86\./.test(String(r?.address || r?.ip || ""));
  return {
    ids: audio.map((r) => r.id).sort().join("|"),
    packets: sum(audio, direction === "send" ? "packetsSent" : "packetsReceived"),
    bytes: sum(audio, direction === "send" ? "bytesSent" : "bytesReceived"),
    energy: sum(sources, "totalAudioEnergy"),
    lost: sum(audio, "packetsLost"),
    concealed: sum(audio, "concealedSamples"),
    jitterMs: audio.some((r) => finite(r.jitter))
      ? Math.max(...audio.map((r) => finite(r.jitter) ? r.jitter * 1000 : 0)) : null,
    rttMs: finite(pair?.currentRoundTripTime) ? pair.currentRoundTripTime * 1000 : null,
    path: pair ? (overlay(local) || overlay(remote) ? "agentnet"
      : local?.candidateType === "relay" || remote?.candidateType === "relay" ? "relay" : "direct") : "unknown",
    protocol: ["udp", "tcp"].includes(local?.protocol) ? local.protocol : "unknown",
    dtls: transports.some((r) => r.dtlsState === "failed") ? "failed"
      : transports.some((r) => r.dtlsState === "connected") ? "connected" : "unknown",
  };
}

export function counterDelta(current, previous, key, elapsed) {
  if (!previous || current.ids !== previous.ids || elapsed < 1000 || elapsed > 30000
    || !finite(current[key]) || !finite(previous[key]) || current[key] < previous[key]) return null;
  return current[key] - previous[key];
}

function boundedStats(pc) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 2000);
    Promise.resolve().then(() => pc.getStats()).then(
      (stats) => { clearTimeout(timer); resolve(stats); },
      () => { clearTimeout(timer); resolve(null); },
    );
  });
}

export class MediaDiagnostics {
  constructor(readStreams, emit) {
    this.readStreams = readStreams;
    this.emit = emit;
    this.previous = new Map();
    this.pcIds = new WeakMap();
    this.nextPc = 0;
    this.active = false;
    this.busy = false;
    this.outbound = Promise.resolve();
  }

  start() {
    this.active = true;
    this.connectionId = globalThis.crypto?.randomUUID?.()
      || `media-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    this.timer = setInterval(() => { this.sample().catch(() => undefined); }, 10000);
  }

  stop() {
    if (!this.active) return;
    this.active = false;
    clearInterval(this.timer);
    this.previous.clear();
    this.send({ version: 1, connectionId: this.connectionId,
      sampledAt: Date.now(), ended: true, streams: [] });
  }

  send(report) {
    // Preserve ordering when hangup/recovery happens during an HTTP report.
    this.outbound = this.outbound.then(() => this.emit(report)).catch(() => undefined);
    return this.outbound;
  }

  async sample() {
    if (!this.active || this.busy) return;
    this.busy = true;
    try {
      const descriptions = this.readStreams();
      const total = descriptions.length;
      const streams = descriptions.slice(0, 64);
      const cache = new Map();
      for (const s of streams) for (const pc of s.pcs) {
        if (!cache.has(pc)) cache.set(pc, boundedStats(pc));
      }
      await Promise.all(cache.values());
      if (!this.active) return;
      const at = Date.now();
      const next = new Map();
      const samples = [];
      for (const s of streams) {
        const tracks = s.mediaStream?.getAudioTracks?.() || [];
        const readings = [];
        for (const pc of s.pcs) {
          const report = await cache.get(pc);
          if (!report) continue;
          if (!this.pcIds.has(pc)) this.pcIds.set(pc, ++this.nextPc);
          const key = `${s.direction}:${s.streamId}:${this.pcIds.get(pc)}`;
          const counters = audioCounters(report, s.direction, tracks.map((t) => t.id));
          const prev = this.previous.get(key);
          const elapsed = prev ? at - prev.at : 0;
          const d = (name) => counterDelta(counters, prev?.counters, name, elapsed);
          readings.push({ ...counters, packetsDelta: d("packets"), bytesDelta: d("bytes"),
            energyDelta: d("energy"), lostDelta: d("lost"), concealedDelta: d("concealed"), sampleMs: elapsed });
          next.set(key, { at, counters });
        }
        const aggregate = (name) => readings.length === s.pcs.length ? sum(readings, name) : null;
        const max = (name) => readings.some((r) => finite(r[name]))
          ? Math.max(...readings.map((r) => finite(r[name]) ? r[name] : 0)) : null;
        samples.push({
          streamId: s.streamId, direction: s.direction, ageMs: s.ageMs,
          subscribed: s.subscribed, muted: s.muted,
          track: !tracks.length ? "missing" : tracks.every((t) => t.readyState === "ended") ? "ended"
            : tracks.every((t) => !t.enabled) ? "disabled" : tracks.every((t) => t.muted) ? "muted" : "live",
          playback: s.direction === "send" ? "na" : !s.element ? "missing"
            : s.element.paused ? "paused" : s.element.muted || s.element.volume === 0 ? "muted" : "playing",
          playError: s.playError || "", pcCount: s.pcs.length,
          stats: !s.pcs.length ? "missing" : readings.length < s.pcs.length ? "unavailable"
            : aggregate("packets") === null ? "unmapped" : "ok",
          ice: s.pcs.some((p) => p.iceConnectionState === "failed" || p.connectionState === "failed") ? "failed"
            : s.pcs.some((p) => p.iceConnectionState === "disconnected") ? "disconnected"
              : s.pcs.length && s.pcs.every((p) => ["connected", "completed"].includes(p.iceConnectionState)) ? "connected" : "unknown",
          dtls: readings.some((r) => r.dtls === "failed") ? "failed"
            : readings.length && readings.every((r) => r.dtls === "connected") ? "connected" : "unknown",
          path: readings.some((r) => r.path === "agentnet") ? "agentnet"
            : readings.some((r) => r.path === "relay") ? "relay"
              : readings.length && readings.every((r) => r.path === "direct") ? "direct" : "unknown",
          packetsDelta: aggregate("packetsDelta"), bytesDelta: aggregate("bytesDelta"),
          energyDelta: aggregate("energyDelta"), lostDelta: aggregate("lostDelta"),
          concealedDelta: aggregate("concealedDelta"), sampleMs: max("sampleMs"),
          jitterMs: max("jitterMs"), rttMs: max("rttMs"),
        });
      }
      if (!this.active) return;
      this.previous = next;
      await this.send({ version: 1, connectionId: this.connectionId, sampledAt: at,
        visibility: document.visibilityState === "visible" ? "visible" : "hidden",
        truncated: total > 64, streams: samples });
    } finally {
      this.busy = false;
    }
  }
}
